import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { 
  Search, 
  Filter, 
  CheckCircle, 
  XCircle, 
  Eye,
  Clock,
  Music as MusicIcon,
  AlertCircle,
  SlidersHorizontal,
  Users,
  Edit,
  Trash2,
  PlayCircle,
  PauseCircle,
  DollarSign,
  Calendar
} from 'lucide-react';
import { musicService } from '../services/api';

const StatusBadge = ({ status }) => {
  const statusConfig = {
    approved: { color: 'bg-green-100 text-green-800 border border-green-200', icon: CheckCircle },
    pending: { color: 'bg-yellow-100 text-yellow-800 border border-yellow-200', icon: Clock },
    rejected: { color: 'bg-red-100 text-red-800 border border-red-200', icon: XCircle },
    draft: { color: 'bg-gray-100 text-gray-800 border border-gray-200', icon: Clock }
  };

  const config = statusConfig[status] || statusConfig.draft;
  const Icon = config.icon;

  return (
    <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-semibold ${config.color}`}>
      <Icon className="h-3 w-3 mr-1.5" />
      {status.charAt(0).toUpperCase() + status.slice(1)}
    </span>
  );
};

const MusicDetailsModal = ({ music, isOpen, onClose, onStatusChange, queryClient }) => {
  const [activeTab, setActiveTab] = useState('details');
  const [loadingStatus, setLoadingStatus] = useState(false);

  const { data: musicDetails } = useQuery({
    queryKey: ['music', music?.id],
    queryFn: () => musicService.getMusic(music.id),
    enabled: !!music && isOpen
  });

  if (!isOpen || !music) return null;

  const handleStatusChange = async (musicId, newStatus) => {
    setLoadingStatus(true);
    try {
      if (newStatus === 'rejected') {
        const reason = window.prompt('Please provide a reason for rejection:');
        if (reason === null) {
          setLoadingStatus(false);
          return;
        }
        await musicService.adminUpdateStatus(musicId, newStatus, reason);
      } else {
        await musicService.adminUpdateStatus(musicId, newStatus);
      }
      
      alert('Music status updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['music'] });
      onClose();
    } catch (error) {
      alert(`Failed to update status: ${error.message}`);
    } finally {
      setLoadingStatus(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <img
              src={music.cover_image_url || (music.cover_image?.url) || music.cover_image || '/music-placeholder.png'}
              alt={music.title}
              className="h-20 w-20 object-cover rounded-lg shadow-xs border border-gray-200"
            />
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{music.title}</h2>
              <p className="text-gray-600 text-sm">by {music.artist_name || music.artist_details?.stage_name || 'Unknown Artist'}</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        <div className="border-b border-gray-200">
          <div className="px-6 flex space-x-8">
            {['details', 'actions'].map(tab => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`py-4 border-b-2 font-medium text-sm transition-colors ${
                  activeTab === tab
                    ? 'border-[#1d1d1d] text-[#1d1d1d]'
                    : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
              >
                {tab === 'details' ? 'Music Details' : 'Actions'}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6 max-h-[60vh] overflow-y-auto">
          {activeTab === 'details' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Basic Information</h3>
                  <dl className="space-y-2">
                    <div><dt className="text-xs text-gray-500">Title</dt><dd className="text-sm font-medium">{music.title}</dd></div>
                    <div><dt className="text-xs text-gray-500">Artist</dt><dd className="text-sm font-medium">{music.artist_name || music.artist_details?.stage_name}</dd></div>
                    <div><dt className="text-xs text-gray-500">Genre</dt><dd className="text-sm font-medium">{music.genre_name}</dd></div>
                    <div><dt className="text-xs text-gray-500">Price</dt><dd className="text-sm font-medium">GHS {music.price}</dd></div>
                  </dl>
                </div>
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Audio Details</h3>
                  <dl className="space-y-2">
                    <div><dt className="text-xs text-gray-500">Status</dt><dd className="text-sm font-medium"><StatusBadge status={music.status} /></dd></div>
                    <div><dt className="text-xs text-gray-500">SVP ID</dt><dd className="text-sm font-medium font-mono">{music.svp_id}</dd></div>
                    <div><dt className="text-xs text-gray-500">Release Date</dt><dd className="text-sm font-medium">{new Date(music.release_date).toLocaleDateString()}</dd></div>
                    <div><dt className="text-xs text-gray-500">Duration</dt><dd className="text-sm font-medium">{formatDuration(music.duration)}</dd></div>
                  </dl>
                </div>
              </div>

              {music.description && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
                  <p className="text-sm text-gray-700 leading-relaxed">{music.description}</p>
                </div>
              )}

              {musicDetails?.featuring_details && musicDetails.featuring_details.length > 0 && (
                <div>
                  <h3 className="text-sm font-medium text-gray-500 mb-2">Featuring Artists</h3>
                  <div className="flex flex-wrap gap-2">
                    {musicDetails.featuring_details.map(artist => (
                      <span key={artist.id} className="inline-flex items-center px-3 py-1 rounded-full bg-blue-100 text-blue-800 text-sm">
                        <Users className="h-3 w-3 mr-1" />
                        {artist.stage_name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Audio Preview */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-2">Audio Preview</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <button
                        onClick={() => {
                          const audio = new Audio(music.preview_audio_url);
                          audio.play();
                        }}
                        className="w-10 h-10 rounded-full bg-[#1d1d1d] text-white flex items-center justify-center hover:bg-gray-800 transition-colors"
                      >
                        <PlayCircle className="h-5 w-5" />
                      </button>
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          Preview Audio ({formatDuration(music.preview_duration || 90)})
                        </p>
                        <p className="text-xs text-gray-500">
                          Click to play preview
                        </p>
                      </div>
                    </div>
                    <span className="text-sm text-gray-500">
                      {music.plays_count || 0} plays
                    </span>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'actions' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-4">Change Music Status</h3>
                <div className="grid grid-cols-3 gap-4">
                  <button
                    onClick={() => handleStatusChange(music.id, 'approved')}
                    disabled={music.status === 'approved' || loadingStatus}
                    className="p-4 bg-green-50 border border-green-200 rounded-lg text-center disabled:opacity-50 hover:bg-green-100 transition-colors"
                  >
                    {loadingStatus ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-green-600 mx-auto mb-2"></div>
                    ) : (
                      <>
                        <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
                        <div className="text-sm font-medium text-green-800">Approve</div>
                        <div className="text-xs text-green-600 mt-1">Make music public</div>
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => handleStatusChange(music.id, 'draft')}
                    disabled={music.status === 'draft' || loadingStatus}
                    className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center disabled:opacity-50 hover:bg-yellow-100 transition-colors"
                  >
                    {loadingStatus ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-600 mx-auto mb-2"></div>
                    ) : (
                      <>
                        <PauseCircle className="h-6 w-6 text-yellow-600 mx-auto mb-2" />
                        <div className="text-sm font-medium text-yellow-800">Put on Hold</div>
                        <div className="text-xs text-yellow-600 mt-1">Change to draft status</div>
                      </>
                    )}
                  </button>
                  
                  <button
                    onClick={() => handleStatusChange(music.id, 'rejected')}
                    disabled={music.status === 'rejected' || loadingStatus}
                    className="p-4 bg-red-50 border border-red-200 rounded-lg text-center disabled:opacity-50 hover:bg-red-100 transition-colors"
                  >
                    {loadingStatus ? (
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-red-600 mx-auto mb-2"></div>
                    ) : (
                      <>
                        <XCircle className="h-6 w-6 text-red-600 mx-auto mb-2" />
                        <div className="text-sm font-medium text-red-800">Reject</div>
                        <div className="text-xs text-red-600 mt-1">Remove from platform</div>
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="border-t border-gray-200 pt-6">
                <h3 className="text-sm font-medium text-gray-500 mb-4">Other Actions</h3>
                <div className="grid grid-cols-2 gap-4">
                  <Link
  to={`/admin/music/${music.id}/edit`}
  className="inline-flex items-center px-4 py-2.5 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md no-underline"
>
  <Edit className="h-4 w-4 mr-2" />
  Edit
</Link>
                  <button 
                    onClick={() => {
                      if (window.confirm('Are you sure you want to delete this music?')) {
                        musicService.deleteMusic(music.id)
                          .then(() => {
                            alert('Music deleted successfully');
                            onClose();
                            queryClient.invalidateQueries({ queryKey: ['music'] });
                          })
                          .catch(() => alert('Error deleting music'));
                      }
                    }}
                    className="p-3 bg-red-50 border border-red-200 rounded-lg text-center hover:bg-red-100 transition-colors"
                  >
                    <Trash2 className="h-5 w-5 text-red-600 mx-auto mb-1" />
                    <div className="text-sm font-medium text-red-800">Delete Music</div>
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default function MusicManagement() {  
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    genre: ''
  });
  const [selectedMusic, setSelectedMusic] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const queryClient = useQueryClient();

  const { data: music = [], isLoading, error } = useQuery({
    queryKey: ['music', filters],
    queryFn: () => musicService.getAll(filters),
    select: (data) => {
      if (Array.isArray(data)) return data;
      if (data?.results) return data.results;
      return [];
    }
  });

  const { data: pendingMusic = [] } = useQuery({
    queryKey: ['pending-music'],
    queryFn: () => musicService.getPending(),
    select: (data) => {
      if (Array.isArray(data)) return data;
      if (data?.results) return data.results;
      return [];
    }
  });

  const updateStatusMutation = useMutation({
    mutationFn: ({ musicId, status }) => 
      musicService.adminUpdateStatus(musicId, status),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['music'] });
      queryClient.invalidateQueries({ queryKey: ['pending-music'] });
    }
  });

  const handleStatusChange = async (musicId, newStatus) => {
    if (newStatus === 'rejected') {
      const reason = window.prompt('Please provide a reason for rejection:');
      if (reason === null) return;
      
      updateStatusMutation.mutate({ 
        musicId, 
        status: newStatus,
        reason 
      });
    } else {
      updateStatusMutation.mutate({ musicId, status: newStatus });
    }
  };

  const handleViewDetails = (music) => {
    setSelectedMusic(music);
    setShowDetailsModal(true);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (error) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <AlertCircle className="mx-auto h-16 w-16 text-red-400 mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Error loading music</h3>
          <p className="text-gray-600 font-medium">Please try again later.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Music Management</h1>
          <p className="text-gray-600 font-medium">Manage and review all music on the platform</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="text-sm text-gray-500 bg-gray-100 px-4 py-2 rounded-xl font-semibold">
            {pendingMusic.length || 0} music pending review
          </div>
          <Link
            to="/admin/upload-music"
            className="bg-gradient-to-r from-[#ff9702] to-[#ffaa33] text-white px-5 py-2.5 rounded-xl text-sm font-semibold hover:shadow-lg transition-all duration-200 shadow-md hover:shadow-lg flex items-center no-underline"
          >
            <MusicIcon className="h-4 w-4 mr-2" />
            Upload Music
          </Link>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xs border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <SlidersHorizontal className="h-5 w-5 mr-2 text-[#1d1d1d]" />
            Filters
          </h3>
          <button
            onClick={() => setFilters({ search: '', status: '', genre: '' })}
            className="text-sm text-gray-500 hover:text-gray-700 font-medium"
          >
            Clear all
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Search Music</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search by title or artist..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="pl-10 w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Status</label>
            <select
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="draft">Draft</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">Genre</label>
            <select
              value={filters.genre}
              onChange={(e) => handleFilterChange('genre', e.target.value)}
              className="w-full rounded-xl border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1d1d1d] focus:border-[#1d1d1d] transition-all duration-200"
            >
              <option value="">All Genres</option>
              <option value="hip-hop">Hip Hop</option>
              <option value="afrobeats">Afrobeats</option>
              <option value="gospel">Gospel</option>
              <option value="r&b">R&B</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilters({ search: '', status: '', genre: '' })}
              className="w-full bg-gray-100 text-gray-700 px-4 py-3 rounded-xl hover:bg-gray-200 transition-all duration-200 font-semibold"
            >
              Reset Filters
            </button>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-xs border border-gray-200 overflow-hidden">
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              All Music ({music.length})
            </h2>
            <div className="text-sm text-gray-500 font-medium">Sorted by: Latest</div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1d1d1d]"></div>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {music.map((music) => (
              <div key={music.id} className="px-6 py-5 hover:bg-gray-50 transition-colors duration-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    <div className="relative">
                      <img
                        src={music.cover_image_url || (music.cover_image?.url) || music.cover_image || '/music-placeholder.png'}
                        alt={music.title}
                        className="h-20 w-20 object-cover rounded-lg shadow-xs border border-gray-200"
                      />
                      {music.status === 'pending' && (
                        <div className="absolute -top-1 -right-1">
                          <div className="bg-yellow-500 rounded-full p-1 shadow-xs border border-white">
                            <Clock className="h-2 w-2 text-white" />
                          </div>
                        </div>
                      )}
                    </div>
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-1">{music.title}</h3>
                      <p className="text-sm text-gray-600 font-medium mb-2">
                        by {music.artist_name || music.artist_details?.stage_name || 'Unknown Artist'}
                      </p>
                      <div className="flex items-center space-x-4">
                        <StatusBadge status={music.status} />
                        <span className="text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full font-medium">
                          {music.genre_name || 'No Genre'}
                        </span>
                        <span className="text-sm font-semibold text-gray-700 flex items-center">
                          <PlayCircle className="h-3 w-3 mr-1" />
                          {formatDuration(music.duration)}
                        </span>
                        <span className="text-sm font-semibold text-[#ff9702] flex items-center">
                          <DollarSign className="h-3 w-3 mr-1" />
                          GHS {music.price || '0.00'}
                        </span>
                        <span className="text-sm text-gray-500 font-medium">SVP ID: {music.svp_id || 'N/A'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center space-x-3">
                    {music.status === 'pending' && (
                      <>
                        <button
                          onClick={() => handleStatusChange(music.id, 'approved')}
                          disabled={updateStatusMutation.isLoading}
                          className="inline-flex items-center px-4 py-2.5 border border-transparent text-sm font-semibold rounded-xl text-white bg-green-600 hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          {updateStatusMutation.isLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          ) : (
                            <CheckCircle className="h-4 w-4 mr-2" />
                          )}
                          Approve
                        </button>
                        <button
                          onClick={() => handleStatusChange(music.id, 'draft')}
                          disabled={updateStatusMutation.isLoading}
                          className="inline-flex items-center px-4 py-2.5 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md"
                        >
                          {updateStatusMutation.isLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-400 mr-2"></div>
                          ) : (
                            <PauseCircle className="h-4 w-4 mr-2" />
                          )}
                          Put on Hold
                        </button>
                        <button
                          onClick={() => handleStatusChange(music.id, 'rejected')}
                          disabled={updateStatusMutation.isLoading}
                          className="inline-flex items-center px-4 py-2.5 border border-transparent text-sm font-semibold rounded-xl text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-md hover:shadow-lg"
                        >
                          {updateStatusMutation.isLoading ? (
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          ) : (
                            <XCircle className="h-4 w-4 mr-2" />
                          )}
                          Reject
                        </button>
                      </>
                    )}
                    
                    <button 
                      onClick={() => handleViewDetails(music)}
                      className="inline-flex items-center px-4 py-2.5 border border-gray-300 text-sm font-semibold rounded-xl text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 shadow-sm hover:shadow-md"
                    >
                      <Eye className="h-4 w-4 mr-2" />
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {music.length === 0 && !isLoading && (
          <div className="text-center py-16">
            <MusicIcon className="mx-auto h-16 w-16 text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No music found</h3>
            <p className="text-gray-600 font-medium mb-4">Try adjusting your search or filter criteria.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => setFilters({ search: '', status: '', genre: '' })}
                className="bg-[#1d1d1d] text-white px-6 py-3 rounded-xl text-sm font-semibold hover:bg-gray-800 transition-all duration-200"
              >
                Clear all filters
              </button>
              <Link
                to="/admin/upload-music"
                className="bg-gradient-to-r from-[#ff9702] to-[#ffaa33] text-white px-6 py-3 rounded-xl text-sm font-semibold hover:shadow-lg transition-all duration-200 no-underline text-center"
              >
                Upload First Music
              </Link>
            </div>
          </div>
        )}
      </div>

      <MusicDetailsModal
        music={selectedMusic}
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedMusic(null);
        }}
        onStatusChange={handleStatusChange}
        queryClient={queryClient}
      />
    </div>
  );
}